/**
 * Health Diary Service (Frontend)
 * 프론트엔드에서 건강 일기 API와 통신하는 서비스
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface DiaryResponse {
  success: boolean;
  data: any;
  message?: string;
  todayDate?: string;
}

/**
 * 오늘의 일기 조회
 */
export const getTodayDiary = async (): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/health-diary/today`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch today's diary: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[HealthDiaryService] Error getting today\'s diary:', error);
    throw error;
  }
};

/**
 * 특정 날짜의 일기 조회
 * @param {string} date - YYYY-MM-DD 형식의 날짜
 */
export const getDiaryByDate = async (date: string): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/health-diary/date/${date}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch diary for ${date}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`[HealthDiaryService] Error getting diary for ${date}:`, error);
    throw error;
  }
};

/**
 * 일기 저장/수정 (Upsert)
 * @param {string} content - 일기 내용
 * @param {string} diaryDate - 일기 날짜 (선택사항, YYYY-MM-DD 형식)
 */
export const saveDiary = async (content: string, diaryDate: string | null = null): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    const payload: { content: string; diaryDate?: string } = { content };
    if (diaryDate) {
      payload.diaryDate = diaryDate;
    }

    console.log('[HealthDiaryService] Sending save request:', { content: content.substring(0, 50) + '...', diaryDate, authToken: !!authToken });

    const response = await fetch(`${API_BASE_URL}/api/health-diary/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`,
      },
      body: JSON.stringify(payload),
    });

    console.log('[HealthDiaryService] Save response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[HealthDiaryService] Save error response:', errorData);
      throw new Error(errorData.message || 'Failed to save diary');
    }

    const data = await response.json();
    console.log('[HealthDiaryService] Save success:', data);
    return data;
  } catch (error) {
    console.error('[HealthDiaryService] Error saving diary:', error);
    throw error;
  }
};

/**
 * 날짜 범위의 일기 목록 조회
 * @param {string} startDate - 시작 날짜 (YYYY-MM-DD)
 * @param {string} endDate - 종료 날짜 (YYYY-MM-DD)
 */
export const getDiariesByRange = async (startDate: string, endDate: string): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    const params = new URLSearchParams({
      startDate,
      endDate,
    });

    const response = await fetch(
      `${API_BASE_URL}/api/health-diary/range?${params}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken || ''}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch diaries for range: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[HealthDiaryService] Error getting diaries by range:', error);
    throw error;
  }
};

/**
 * 월별 일기 목록 조회
 * @param {number} year - 연도
 * @param {number} month - 월 (1-12)
 */
export const getDiariesByMonth = async (year: number, month: number): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(
      `${API_BASE_URL}/api/health-diary/month/${year}/${month}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken || ''}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch diaries for month: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(
      `[HealthDiaryService] Error getting diaries for ${year}-${month}:`,
      error
    );
    throw error;
  }
};

/**
 * 일기 삭제
 * @param {string} diaryId - 삭제할 일기 ID
 */
export const deleteDiary = async (diaryId: string): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    
    console.log('[HealthDiaryService] Sending delete request:', { 
      diaryId, 
      authToken: !!authToken 
    });
    
    const response = await fetch(`${API_BASE_URL}/api/health-diary/${diaryId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`,
      },
    });

    console.log('[HealthDiaryService] Delete response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[HealthDiaryService] Delete error response:', errorData);
      throw new Error(`Failed to delete diary: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[HealthDiaryService] Delete success:', data);
    return data;
  } catch (error) {
    console.error('[HealthDiaryService] Error deleting diary:', error);
    throw error;
  }
};

/**
 * 일기 통계 조회
 */
export const getDiaryStats = async (): Promise<DiaryResponse> => {
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/health-diary/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken || ''}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch diary stats: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[HealthDiaryService] Error getting diary stats:', error);
    throw error;
  }
};
